import filterDuplicates from '~/utils/filter-duplicates';
import { ProductWithStock, VariantWithStock } from './use-product-availability';

export type OptionValuesWithStock = {
  [key: string]: Queries.OptionValuesFragmentFragment & {
    inStock: {
      [key: string]: boolean;
    };
  };
};

export const useOptionValue = (
  product: Queries.ProductCardFragmentFragment | Queries.ProductDetailsFragmentFragment | ProductWithStock | null,
  optionName: 'color' | 'size'
): [Queries.OptionValuesFragmentFragment[], OptionValuesWithStock | null] => {
  if (!product) return [[], null];
  let optionValuesWithStock: OptionValuesWithStock | null = null;
  if (productIsProductWithStock(product)) {
    optionValuesWithStock = product.variants
      ?.map((variant: VariantWithStock) => ({
        optionValues: variant?.optionValues,
        inStock: variant?.inStock,
      }))
      .reduce((acc: OptionValuesWithStock, { optionValues, inStock }) => {
        const optionValue = optionValues?.find((ov) => ov?.option_type?.[0]?.slug === optionName);
        const otherOptionValue = optionValues?.find((ov) => ov?.option_type?.[0]?.slug !== optionName);
        if (!optionValue || !otherOptionValue) return acc;
        if (optionValue.presentation && otherOptionValue.presentation) {
          if (!acc[optionValue.presentation]) {
            // if exists, add inStock for each
            acc[optionValue.presentation] = {
              ...optionValue,
              inStock: {
                [otherOptionValue.presentation]: inStock,
              },
            };
            return acc;
          } else {
            acc[optionValue.presentation].inStock[otherOptionValue.presentation] = inStock;
            return acc;
          }
        }

        return acc;
      }, {});
  }
  const allOptionValues =
    Array.isArray(product.variants) && product?.variants?.map((variant) => variant?.optionValues)?.flat();

  const availableOptionValues: Queries.OptionValuesFragmentFragment[] = filterDuplicates(allOptionValues, 'slug');

  const uniqueOptionValues = availableOptionValues?.filter(
    ({ option_type = [] }) => option_type?.length && option_type[0]?.slug === optionName
  );
  return [uniqueOptionValues, optionValuesWithStock];
};

const productIsProductWithStock = (
  product: Queries.ProductCardFragmentFragment | Queries.ProductDetailsFragmentFragment | ProductWithStock
): product is ProductWithStock => {
  return product?.variants?.every((variant) => variant && 'inStock' in variant) ?? false;
};
